import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Workflow, Zap, Link2, GraduationCap, CheckCircle2 } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const STEPS = [{
  icon: Workflow,
  title: 'Discovery & Mapping',
  desc: 'We sit down with your team to understand your unique processes, bottlenecks, and the tools you rely on daily.'
}, {
  icon: Zap,
  title: 'Custom Build-out',
  desc: 'Our engineers design and deploy custom automations and agents tailored specifically to your mapped workflows.'
}, {
  icon: Link2,
  title: 'Deep Integration',
  desc: 'We wire Giga directly into your existing stack—whether it’s legacy on-prem systems or modern SaaS.'
}, {
  icon: GraduationCap,
  title: 'Training & Tuning',
  desc: 'We onboard your team, run workshops, and continuously refine the agents as your operations evolve.'
}];
const OUTCOMES = [{
  title: 'Automated Client Reporting',
  desc: 'Giga pulls data from Salesforce, Stripe, and internal databases to generate and email weekly client reports without human intervention.'
}, {
  title: 'RevOps Pipelines',
  desc: 'Seamlessly sync lead data across marketing and sales tools, automatically triggering personalized outreach sequences.'
}, {
  title: 'Support Triage',
  desc: 'Intelligently categorize, route, and draft initial responses for incoming support tickets based on historical resolution data.'
}, {
  title: 'Finance Reconciliation',
  desc: 'Automatically match invoices with bank statements and flag discrepancies for human review, saving hours of manual data entry.'
}];
export function Bespoke() {
  useScreenInit();
  return <div className="min-h-screen bg-[#1C1510] flex flex-col pt-24 pb-12 relative overflow-hidden" data-id="element-7043" data-mp="kuksttcco19h">
      {/* Dark texture overlay */}
      <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none z-0" data-id="element-7044" data-mp="1iarwgkv2uf8b" />

      {/* Soft warm glow */}
      <div aria-hidden="true" className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.08] blur-[120px] z-0" data-id="element-7045" data-mp="1dhp2ibme13rl" />

      <div className="relative z-10 flex-1 flex flex-col" data-id="element-7046" data-mp="21da9fip664u4">
        {/* Hero Section */}
        <section className="px-6 md:px-10 py-16 md:py-24 max-w-5xl mx-auto text-center" data-id="element-7047" data-mp="1nnqayp2mftqm">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-7048" data-mp="1ro0k2alpn09p">
            <span className="inline-block text-xs font-bold tracking-[3px] uppercase text-[#EA580C] mb-6" data-id="element-7049" data-mp="1ioddlae4j49s">
              Custom Engagement
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-title font-bold text-white tracking-tight leading-[1.1] mb-8" data-id="element-7050" data-mp="14q5m3hzuwv60">
              Your workflows, <br className="hidden md:block" data-id="element-7051" data-mp="2z1kfc8s2tcqi" />
              <span className="text-neutral-400" data-id="element-7052" data-mp="2z2vefvaktwba">built by our team.</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-400 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-7053" data-mp="3335njgto12ud">
              For teams with complex operations. We don't just hand you a tool;
              we partner with you to map, build, and deploy custom AI agents
              that integrate deeply into your stack.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7054" data-mp="117zuigsyl2kt">
              <Link to="/talk-to-sales" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors shadow-lg shadow-[#EA580C]/20 flex items-center justify-center gap-2" data-id="element-7055" data-mp="wdc42cee9y3l">
                Book a call
                <ArrowRight size={18} data-id="element-7056" data-mp="1k3pvc0p5nsx4" />
              </Link>
              <Link to="/contact" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold transition-colors flex items-center justify-center" data-id="element-7057" data-mp="1f665e3j3hrpc">
                Contact us
              </Link>
            </div>
          </motion.div>
        </section>

        {/* How it Works */}
        <section className="px-6 md:px-10 py-16 md:py-24 bg-[#2B211A]/50 border-y border-white/5" data-id="element-7058" data-mp="3jb6brbpxgz42">
          <div className="max-w-6xl mx-auto" data-id="element-7059" data-mp="3nc89tp83z54g">
            <div className="text-center mb-16" data-id="element-7060" data-mp="3scmklf13gt66">
              <h2 className="text-3xl md:text-4xl font-title font-bold text-white mb-4" data-id="element-7061" data-mp="3sdxnzdqmnywr">
                How the engagement works
              </h2>
              <p className="text-neutral-400 max-w-2xl mx-auto" data-id="element-7062" data-mp="2znywzcsvhrpg">
                A structured, hands-on approach to automating your most critical
                business processes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-7063" data-mp="39lms43ue5185">
              {STEPS.map((step, i) => {
              const Icon = step.icon;
              return <motion.div key={i} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.5,
                delay: i * 0.1
              }} className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden group hover:border-[#EA580C]/50 transition-colors" data-id="element-7064" data-mp="3eeoailtt3iwh" data-mpi={i}>
                    <div className="absolute top-0 right-0 p-4 text-[100px] font-title font-bold text-white/[0.02] leading-none select-none pointer-events-none group-hover:text-[#EA580C]/[0.05] transition-colors" data-id="element-7065" data-mp="1zg0sq29wmjx3">
                      {i + 1}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#1C1510] border border-white/10 flex items-center justify-center mb-6 relative z-10" data-id="element-7066" data-mp="1qldq1dvmkloj">
                      <Icon className="w-6 h-6 text-[#EA580C]" data-id="element-7067" data-mp="1gq2awugb1cg9" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 relative z-10" data-id="element-7068" data-mp="1bqpoubx9c9eq">
                      {step.title}
                    </h3>
                    <p className="text-sm text-neutral-400 leading-relaxed relative z-10" data-id="element-7069" data-mp="17p4div0dg36n">
                      {step.desc}
                    </p>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section className="px-6 md:px-10 py-16 md:py-24 max-w-6xl mx-auto" data-id="element-7070" data-mp="o3krph8y1jbe">
          <div className="mb-16" data-id="element-7071" data-mp="t1m5pnriisg0">
            <h2 className="text-3xl md:text-4xl font-title font-bold text-white mb-4" data-id="element-7072" data-mp="t2owzwsqywwg">
              What we build
            </h2>
            <p className="text-neutral-400 max-w-2xl" data-id="element-7073" data-mp="1gtkwlg3g2we9">
              Examples of custom solutions we've deployed for enterprise
              partners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7074" data-mp="171euvene50lv">
            {OUTCOMES.map((outcome, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: i % 2 === 0 ? -20 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5
          }} className="flex gap-4 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/[0.07] transition-colors" data-id="element-7075" data-mp="1bz6uyvy6vt15" data-mpi={i}>
                <div className="mt-1 shrink-0" data-id="element-7076" data-mp="2nlo3frv8b3o4">
                  <CheckCircle2 className="w-6 h-6 text-[#EA580C]" data-id="element-7077" data-mp="2nkcpx0bzehjt" />
                </div>
                <div data-id="element-7078" data-mp="2inmbehftru0i">
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-7079" data-mp="2in4dzhhff9r0">
                    {outcome.title}
                  </h3>
                  <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-7080" data-mp="1uytkize28ccr">
                    {outcome.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* Proof & Closing CTA */}
        <section className="px-6 md:px-10 py-16 md:py-24 bg-[#EA580C] text-center mt-auto" data-id="element-7081" data-mp="1zv1ttw5ktbc0">
          <div className="max-w-3xl mx-auto" data-id="element-7082" data-mp="3g9a7104gi8az">
            <h2 className="text-3xl md:text-5xl font-title font-bold text-white mb-6" data-id="element-7083" data-mp="3gcfts8pm4y3j">
              Ready to automate your operations?
            </h2>
            <p className="text-lg text-white/80 mb-10" data-id="element-7084" data-mp="19n45ryp6k3jm">
              Let's discuss your workflows and see if a Custom engagement is
              right for your team.
            </p>
            <Link to="/talk-to-sales" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#1C1510] hover:bg-black text-white font-bold transition-colors shadow-xl" data-id="element-7085" data-mp="1im92czh6lmt5">
              Talk to us
              <ArrowRight size={18} data-id="element-7086" data-mp="1sddfrur7kzlv" />
            </Link>
          </div>
        </section>
      </div>
    </div>;
}